<script setup lang="ts">
/**
 * Таблица фича-стендов — макет 90:787: строка 52, шапка 34 моноширинная,
 * действия справа иконками.
 *
 * Колонки «стенд» в макете — адрес окружения, здесь репозиторий и ветка: хост
 * знает CI, а трекер только нажимает джобу и хоста в ответе не получает
 * (записано в docs/design-gaps.md).
 */
import { ExternalLink, RefreshCw, Server, X } from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'

import { formatUptime, initials, isIdle } from '../format'
import { ACTION_STATUS, type Stand } from '../api'

defineProps<{ stands: Stand[]; busyId?: string }>()

const emit = defineEmits<{ redeploy: [stand: Stand]; drop: [stand: Stand] }>()

const { t } = useI18n()

const uptimeLabels = () => ({
  days: t('devops.envs.uptime.days'),
  hours: t('devops.envs.uptime.hours'),
  minutes: t('devops.envs.uptime.minutes'),
})

/** Состояние стенда одним словом: собирается, работает или простаивает. */
const stateOf = (stand: Stand): 'building' | 'idle' | 'active' => {
  if (stand.status === ACTION_STATUS.pending) return 'building'

  return isIdle(stand.raisedAt) ? 'idle' : 'active'
}
</script>

<template>
  <div class="stands">
    <div class="stands__head">
      <span class="stands__col stands__col--stand">{{ t('devops.envs.columns.stand') }}</span>
      <span class="stands__col stands__col--issue">{{ t('devops.envs.columns.issue') }}</span>
      <span class="stands__col stands__col--branch">{{ t('devops.envs.columns.branch') }}</span>
      <span class="stands__col stands__col--actor">{{ t('devops.envs.columns.actor') }}</span>
      <span class="stands__col stands__col--uptime">{{ t('devops.envs.columns.uptime') }}</span>
      <span class="stands__spacer" />
      <span class="stands__col">{{ t('devops.envs.columns.actions') }}</span>
    </div>

    <div v-for="stand in stands" :key="stand.id" class="stands__row">
      <Server class="stands__icon" :size="16" />
      <span class="stands__cell stands__cell--stand">{{ stand.projectPath }}</span>
      <span class="stands__cell stands__cell--issue">
        <template v-if="stand.issueKey">
          <RouterLink class="stands__link" :to="`/issue/${stand.issueKey}`">
            {{ stand.issueKey }}
          </RouterLink>
          {{ stand.issueTitle }}
        </template>
        <span v-else class="stands__muted">{{ t('devops.envs.noIssue') }}</span>
      </span>
      <span class="stands__cell stands__cell--branch">{{ stand.branch }}</span>
      <span class="stands__cell stands__cell--actor" :title="stand.actor">
        {{ initials(stand.actor) }}
      </span>
      <span class="stands__cell stands__cell--uptime">
        {{
          stateOf(stand) === 'building'
            ? t('devops.envs.state.buildingUptime')
            : formatUptime(stand.raisedAt, uptimeLabels())
        }}
      </span>
      <span class="stands__badge" :class="`stands__badge--${stateOf(stand)}`">
        {{ t(`devops.envs.state.${stateOf(stand)}`) }}
      </span>

      <span class="stands__spacer" />

      <a
        v-if="stand.pipelineUrl"
        class="stands__action"
        :href="stand.pipelineUrl"
        target="_blank"
        rel="noopener"
        :aria-label="t('devops.envs.actions.open')"
        :title="t('devops.envs.actions.open')"
      >
        <ExternalLink :size="15" />
      </a>
      <button
        type="button"
        class="stands__action"
        :disabled="busyId === stand.id"
        :aria-label="t('devops.envs.actions.redeploy')"
        :title="t('devops.envs.actions.redeploy')"
        @click="emit('redeploy', stand)"
      >
        <RefreshCw :size="15" />
      </button>
      <button
        type="button"
        class="stands__action stands__action--danger"
        :disabled="busyId === stand.id"
        :aria-label="t('devops.envs.actions.drop')"
        :title="t('devops.envs.actions.drop')"
        @click="emit('drop', stand)"
      >
        <X :size="15" />
      </button>
    </div>
  </div>
</template>

<style scoped>
.stands {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xl);
  background: var(--color-surface);
  overflow: hidden;
}

.stands__head,
.stands__row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 16px;
  border-bottom: 1px solid var(--color-border);
}

.stands__head {
  height: 34px;
  background: var(--color-bg-muted);
  color: var(--color-text-muted);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-overline);
  letter-spacing: 0.04em;
}

.stands__row {
  height: 52px;
}

.stands__row:last-child {
  border-bottom: none;
}

.stands__icon {
  flex-shrink: 0;
  color: var(--color-text-muted);
}

.stands__col--stand,
.stands__cell--stand {
  width: 240px;
}

.stands__col--issue,
.stands__cell--issue {
  width: 200px;
}

.stands__col--branch,
.stands__cell--branch {
  width: 180px;
}

.stands__col--actor,
.stands__cell--actor {
  width: 40px;
}

.stands__col--uptime,
.stands__cell--uptime {
  width: 76px;
}

.stands__cell {
  overflow: hidden;
  color: var(--color-text-muted);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-body-sm);
  white-space: nowrap;
  text-overflow: ellipsis;
}

.stands__cell--stand {
  color: var(--color-text-primary);
}

/* Задача — обычным шрифтом: это единственная колонка с человеческим текстом,
   и моноширинный делает её нечитаемой. */
.stands__cell--issue {
  color: var(--color-text-secondary);
  font-family: inherit;
  font-size: var(--font-size-body);
}

.stands__link {
  color: var(--color-primary);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-body-sm);
  text-decoration: none;
}

.stands__link:hover {
  text-decoration: underline;
}

.stands__muted {
  color: var(--color-text-muted);
}

.stands__spacer {
  flex: 1;
  min-width: 0;
}

.stands__badge {
  display: inline-flex;
  align-items: center;
  height: 20px;
  padding: 0 8px;
  border-radius: var(--radius-sm);
  font-size: var(--font-size-overline);
  font-weight: var(--font-weight-medium);
  white-space: nowrap;
}

.stands__badge--active {
  background: var(--color-success-tint);
  color: var(--color-success-strong);
}

.stands__badge--building {
  background: var(--color-info-tint);
  color: var(--color-info-strong);
}

.stands__badge--idle {
  background: var(--color-warning-tint);
  color: var(--color-warning-strong);
}

.stands__action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: none;
  color: var(--color-text-muted);
  cursor: pointer;
}

.stands__action:hover {
  color: var(--color-text-primary);
}

.stands__action--danger:hover {
  color: var(--color-danger);
}

.stands__action:disabled {
  color: var(--color-text-disabled);
  cursor: default;
}
</style>
